/**
 * Human-readable rendering of an EDI interchange date/time (ISA09 + ISA10).
 *
 * The wire format is bare digits — `260715 1705` — which nobody can read at a
 * glance, and it was displayed verbatim in the EDI transaction viewer.
 *
 * Two things to know about these values:
 *
 * - **No timezone.** ISA09/ISA10 carry the sender's local wall clock with no
 *   offset, so the digits are formatted as-is. Everything here builds the `Date`
 *   from numeric components (never by parsing a string), which keeps it local
 *   and avoids the UTC round-trip that shifts a date by a day.
 * - **Two-digit year.** ISA09 is `YYMMDD`. X12's century convention maps 00–49
 *   to the 2000s and 50–99 to the 1900s. Some payloads send `CCYYMMDD` instead,
 *   so both lengths are accepted.
 */

/** Split point for X12's two-digit years: 00–49 → 2000s, 50–99 → 1900s. */
const CENTURY_PIVOT = 50;

interface InterchangeMoment {
  when: Date;
  /** False when the interchange carried a date but no time. */
  hasTime: boolean;
}

/**
 * Format an interchange date/time for display, e.g. `"Wed, Jul 15, 5:05 PM"`.
 *
 * The year is included **only when it isn't the current year**: on a draft
 * you're working today it's noise, but on an older order it's the difference
 * between "recent" and "last year", so it can't just be dropped.
 *
 * Returns the raw digits unchanged when they don't parse, and null when there's
 * nothing to show — a malformed interchange header must never hide the value or
 * blank the field.
 *
 * @param now Reference point for the current year. Injectable for tests.
 */
export function formatEdiInterchangeDateTime(
  interchangeDate: string | undefined,
  interchangeTime: string | undefined,
  now: Date = new Date(),
): string | null {
  const raw = [interchangeDate, interchangeTime]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');
  if (!raw) return null;

  const moment = parseInterchangeMoment(interchangeDate, interchangeTime);
  if (!moment) return raw;

  return moment.when.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(moment.when.getFullYear() !== now.getFullYear() && {
      year: 'numeric',
    }),
    ...(moment.hasTime && { hour: 'numeric', minute: '2-digit' }),
  });
}

/** Calendar parts of ISA09, before they're checked against a real calendar. */
interface DateParts {
  year: number;
  month: number;
  day: number;
}

/** Wall-clock parts of ISA10. `hasTime` is false when the header carried none. */
interface TimeParts {
  hour: number;
  minute: number;
  hasTime: boolean;
}

/**
 * Parse `YYMMDD`/`CCYYMMDD` plus optional `HHMM`/`HHMMSS` into a local Date.
 * Returns null for anything that isn't a real calendar moment, so the caller
 * can fall back to the raw digits.
 */
function parseInterchangeMoment(
  interchangeDate: string | undefined,
  interchangeTime: string | undefined,
): InterchangeMoment | null {
  const date = parseDateParts(interchangeDate?.trim() ?? '');
  const time = parseTimeParts(interchangeTime?.trim() ?? '');
  if (!date || !time) return null;

  const when = new Date(
    date.year,
    date.month - 1,
    date.day,
    time.hour,
    time.minute,
  );
  // JS rolls impossible dates forward (Jul 32 → Aug 1); reject rather than lie.
  if (
    when.getFullYear() !== date.year ||
    when.getMonth() !== date.month - 1 ||
    when.getDate() !== date.day
  ) {
    return null;
  }

  return { when, hasTime: time.hasTime };
}

/** Split ISA09 into calendar parts. Null when it isn't 6 or 8 digits. */
function parseDateParts(date: string): DateParts | null {
  if (!/^(\d{6}|\d{8})$/.test(date)) return null;

  return {
    year:
      date.length === 8
        ? Number(date.slice(0, 4))
        : expandTwoDigitYear(Number(date.slice(0, 2))),
    month: Number(date.slice(-4, -2)),
    day: Number(date.slice(-2)),
  };
}

/**
 * Split ISA10 into wall-clock parts. An absent time is legal (midnight, flagged
 * so the caller can omit it); a time that's present but unparseable is not —
 * showing the raw digits beats rendering a confident midnight.
 *
 * Seconds are never displayed but must still be valid: an `HHMMSS` of `170560`
 * is a header we don't understand, and rendering it as 5:05 PM would present a
 * guess as a fact.
 */
function parseTimeParts(time: string): TimeParts | null {
  if (!time) return { hour: 0, minute: 0, hasTime: false };
  if (!/^(\d{4}|\d{6})$/.test(time)) return null;

  const hour = Number(time.slice(0, 2));
  const minute = Number(time.slice(2, 4));
  const second = time.length === 6 ? Number(time.slice(4, 6)) : 0;
  if (hour > 23 || minute > 59 || second > 59) return null;

  return { hour, minute, hasTime: true };
}

/** Apply X12's century convention to a two-digit year. */
function expandTwoDigitYear(year: number): number {
  return year < CENTURY_PIVOT ? 2000 + year : 1900 + year;
}
